// Offline call mode: queue call / counselling POSTs when the network is gone, replay them when it returns,
// and serve the last-seen GET responses for the caller's own screens so the phone app stays usable.
const QKEY = "rcrm_offline_queue";
const CPFX = "rcrm_oc:";
const MAX_CACHE_BYTES = 150_000;   // never serialise big lists on the main thread
const QUEUEABLE = [/^\/leads\/[^/]+\/call$/, /^\/leads\/[^/]+\/counselling-touch$/, /^\/leads\/[^/]+\/note$/, /^\/leads\/[^/]+\/stage$/];
const CACHEABLE = [/^\/myleads/, /^\/queue/, /^\/leads\/[^/]+(\/[a-z-]+)?$/, /^\/home/, /^\/me$/, /^\/notifications/, /^\/followups/, /^\/users/];
const listeners = new Set();

export const readQueue = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQueue = (q) => { localStorage.setItem(QKEY, JSON.stringify(q)); listeners.forEach((f) => f(q)); };
export const onQueueChange = (f) => { listeners.add(f); return () => listeners.delete(f); };
const path = (cfg) => (cfg.url || "").replace(cfg.baseURL || "", "").split("?")[0];
const ckey = (cfg) => CPFX + path(cfg) + (cfg.params ? JSON.stringify(cfg.params) : "");
const isNetErr = (e) => !e.response && (e.code === "ERR_NETWORK" || e.message === "Network Error" || !navigator.onLine);

export function enqueue(cfg) {
  const q = readQueue();
  let body = {}; try { body = typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data || {}; } catch { body = {}; }
  q.push({ id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, method: cfg.method, url: path(cfg), body: { ...body, client_at: new Date().toISOString() }, at: new Date().toISOString() });
  writeQueue(q);
}

export function cacheGet(cfg, data) {
  if (!CACHEABLE.some((r) => r.test(path(cfg)))) return;
  const run = () => { try { const s = JSON.stringify({ data, at: Date.now() }); if (s.length <= MAX_CACHE_BYTES) localStorage.setItem(ckey(cfg), s); } catch { /* quota */ } };
  (window.requestIdleCallback || ((f) => setTimeout(f, 300)))(run);
}
export function readCache(cfg) { try { return JSON.parse(localStorage.getItem(ckey(cfg)) || "null"); } catch { return null; } }
export const canQueue = (cfg) => (cfg.method || "").toLowerCase() === "post" && QUEUEABLE.some((r) => r.test(path(cfg)));

let syncing = false;
export async function syncQueue(instance) {
  if (syncing || !navigator.onLine) return { synced: 0 };
  syncing = true;
  let synced = 0, failed = 0;
  try {
    for (const item of readQueue()) {
      try {
        await instance.post(item.url, item.body, { headers: { "X-Offline-Replay": "1" } });
        writeQueue(readQueue().filter((x) => x.id !== item.id)); synced++;
      } catch (e) {
        if (isNetErr(e)) break;
        writeQueue(readQueue().filter((x) => x.id !== item.id)); failed++;
      }
    }
  } finally { syncing = false; }
  return { synced, failed };
}

export function attachOffline(instance) {
  instance.interceptors.response.use(
    (res) => { if ((res.config.method || "").toLowerCase() === "get") cacheGet(res.config, res.data); return res; },
    (err) => {
      const cfg = err.config || {};
      if (!isNetErr(err) || cfg.headers?.["X-Offline-Replay"]) return Promise.reject(err);
      if (canQueue(cfg)) { enqueue(cfg); return Promise.resolve({ data: { ok: true, offline: true, queued: readQueue().length }, status: 202, offline: true, config: cfg }); }
      if ((cfg.method || "").toLowerCase() === "get") { const hit = readCache(cfg); if (hit) return Promise.resolve({ data: hit.data, status: 200, offline: true, cachedAt: hit.at, config: cfg }); }
      return Promise.reject(err);
    });
  return instance;
}
